import { useFieldArray, useFormContext } from 'react-hook-form';
import React, { useRef } from 'react';
import {
    Box,
    Stack,
    TextFieldProps,
    Typography,
    IconButton,
    ImageList,
    ImageListItem,
} from '@mui/material';
import FilterIcon from '@mui/icons-material/Filter';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

export interface ImagesProps {
    name: string;
    required?: boolean;
    regEx?: RegExp;
    showErrors?: boolean;
    chipSx?: Record<string, any>;
    inputProps?: Partial<TextFieldProps>;
    readOnly?: boolean;
}

const getSize = (file: File) => {
    return new Promise(resolve => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = function () {
            resolve({
                // @ts-ignore
                width: this.width as number,
                // @ts-ignore
                height: this.height as number,
            });
        };
        img.src = objectUrl;
    });
};

const toBase64 = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            resolve(reader.result as string);
        };
        reader.onerror = reject;
    });

export const Images: React.FC<ImagesProps> = props => {
    const { name, readOnly = false } = props;
    const { control } = useFormContext();

    const {
        fields: files,
        remove,
        append,
    } = useFieldArray<Record<string, any>, string, string>({
        name,
        control,
    });

    const handleImgChange: React.ChangeEventHandler<HTMLInputElement> = e => {
        if (e.target.files) {
            Array.from(e.target.files).forEach(file => {
                if (!file.type.startsWith('image/')) {
                    // Skip any non-image even if user bypasses accept filter
                    return;
                }
                toBase64(file).then((data: string) => {
                    const contentStart = data.indexOf(',');
                    // Always image here
                    // @ts-ignore
                    getSize(file).then(({ width, height }) => {
                        append({
                            name: file.name,
                            contentType: file.type,
                            content: data.substring(contentStart + 1),
                            width: width,
                            height: height,
                        });
                    });
                });
            });
            e.target.value = '';
        }
    };
    const inputRef = useRef<HTMLInputElement>(null);

    // No longer need drag and drop functionality

    const hasAny = files.filter(f => !readOnly || !!f.url).length > 0;

    return (
        <Stack direction="column" gap={2} sx={{ position: 'relative' }}>
            {!hasAny && !readOnly && (
                <ImageList cols={3} gap={8} sx={{ overflow: 'visible' }}>
                    <ImageListItem>
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                aspectRatio: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                bgcolor: '#F6F6FB',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                                p: 2,
                            }}
                            onClick={() => inputRef.current?.click()}
                            data-testid="upload-images-empty"
                        >
                            <AddIcon
                                sx={{ fontSize: 40, color: '#9198AA', mb: 1 }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ color: '#2B395B', textAlign: 'center' }}
                            >
                                Upload Images
                            </Typography>
                        </Box>
                    </ImageListItem>
                </ImageList>
            )}

            {hasAny && (
                <ImageList cols={3} gap={8} sx={{ overflow: 'visible' }}>
                    {files
                        .filter(f => !readOnly || !!f.url)
                        .map((file, index) => (
                            <ImageItem
                                key={file.id}
                                file={file}
                                readOnly={readOnly}
                                onRemove={() => !readOnly && remove(index)}
                            />
                        ))}
                    {!readOnly && (
                        <ImageListItem>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    aspectRatio: '1/1',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: '#F6F6FB',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    boxShadow:
                                        '0px 2px 8px rgba(0, 0, 0, 0.15)',
                                }}
                                onClick={() => inputRef.current?.click()}
                                data-testid="upload-more-images"
                            >
                                <AddIcon
                                    sx={{ fontSize: 40, color: '#9198AA' }}
                                />
                            </Box>
                        </ImageListItem>
                    )}
                </ImageList>
            )}

            <Box
                component="input"
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/*"
                onChange={handleImgChange}
                ref={inputRef}
                sx={{ display: 'none' }}
            />
        </Stack>
    );
};

interface ImageItemProps {
    file: any;
    readOnly: boolean;
    onRemove: () => void;
}

const ImageItem: React.FC<ImageItemProps> = ({ file, readOnly, onRemove }) => {
    const { name, contentType, content, url } = file;
    const isImage = contentType?.startsWith('image/');
    const src = isImage
        ? content
            ? `data:${contentType};base64,${content}`
            : url
        : undefined;

    return (
        <ImageListItem>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    aspectRatio: '1/1',
                    borderRadius: '8px',
                    bgcolor: '#F3F5F8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover .delete-button': {
                        opacity: 1,
                    },
                }}
                onClick={() => {
                    if (url) window.open(url, '_blank');
                }}
                style={{ cursor: url ? 'pointer' : 'default' }}
            >
                {isImage && src ? (
                    <Box
                        component="img"
                        src={src}
                        alt={name}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                ) : (
                    <FilterIcon sx={{ fontSize: 40, color: '#9198AA' }} />
                )}

                {!readOnly && (
                    <IconButton
                        className="delete-button"
                        size="small"
                        onClick={e => {
                            e.stopPropagation();
                            onRemove();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                            },
                            opacity: 0,
                            transition: 'opacity 0.2s ease-in-out',
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </ImageListItem>
    );
};

export default Images;
